// Utility functions for translating slugs when language changes
import { extractCarIdFromSlug, extractCarSlugComponents } from '../lib/seo'
import { extractCarDetailFromUrl } from './seoUrls'

/**
 * Translate a car detail URL slug from one language to another
 */
export function translateCarSlug(currentSlug: string, fromLang: string, toLang: string): string {
  // Language prefixes mapping
  const prefixes = {
    fr: 'location-voiture',
    en: 'rent-car',
    ar: 'تأجير-سيارة'
  }

  // Extract car ID from the current slug
  const carId = extractCarIdFromSlug(currentSlug)
  if (!carId) {
    return currentSlug // Return original if we can't extract ID
  }

  // Extract slug components
  const components = extractCarSlugComponents(currentSlug)
  if (!components) {
    return currentSlug // Return original if we can't extract components
  }

  // Get the new prefix for the target language
  const newPrefix = prefixes[toLang as keyof typeof prefixes] || prefixes.fr

  // Reconstruct the slug with the new language prefix
  const { carName, category } = components
  
  // Clean and format parts - with null checks
  const cleanPart = (part: string | null) => {
    if (!part) return ''
    return part.toLowerCase()
      .replace(/[^a-z0-9\u0600-\u06FF]+/g, '-') // Support Arabic characters
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }

  // Create new slug with translated prefix
  const slugParts = [
    newPrefix,
    carName || 'car', // Use carName or fallback
    cleanPart(category) || 'category', // Use category or fallback
    carId.slice(0, 8) // Use first 8 characters of UUID for uniqueness
  ].filter(Boolean) // Remove empty parts

  const newSlug = slugParts
    .map(part => typeof part === 'string' ? cleanPart(part) : part)
    .join('-')
    .replace(/-+/g, '-') // Remove multiple consecutive dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes

  return newSlug
}

/**
 * Translate a location-based car detail URL slug from one language to another
 */
export function translateLocationCarSlug(currentSlug: string, fromLang: string, toLang: string): string {
  // Language prefixes mapping for location-based URLs
  const prefixes = {
    fr: 'location-voiture',
    en: 'rent-car',
    ar: 'تأجير-سيارة'
  }

  // Extract car ID from the current slug
  const carId = extractCarIdFromSlug(currentSlug)
  if (!carId) {
    return currentSlug // Return original if we can't extract ID
  }

  // Extract slug components
  const components = extractCarSlugComponents(currentSlug)
  if (!components) {
    return currentSlug // Return original if we can't extract components
  }

  // Get the new prefix for the target language
  const newPrefix = prefixes[toLang as keyof typeof prefixes] || prefixes.fr

  // Reconstruct the slug with the new language prefix
  const { carName, category } = components
  
  // Clean and format parts - with null checks
  const cleanPart = (part: string | null) => {
    if (!part) return ''
    return part.toLowerCase()
      .replace(/[^a-z0-9\u0600-\u06FF]+/g, '-') // Support Arabic characters
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }

  // Create new slug with translated prefix
  const slugParts = [
    newPrefix,
    carName || 'car', // Use carName or fallback
    cleanPart(category) || 'category', // Use category or fallback
    carId.slice(0, 8) // Use first 8 characters of UUID for uniqueness
  ].filter(Boolean) // Remove empty parts

  const newSlug = slugParts
    .map(part => typeof part === 'string' ? cleanPart(part) : part)
    .join('-')
    .replace(/-+/g, '-') // Remove multiple consecutive dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes

  return newSlug
}

/**
 * Check if a URL path is a car detail page
 */
export function isCarDetailPage(pathname: string): boolean {
  // Check for car detail patterns
  const carDetailPatterns = [
    /^\/[a-z]{2}\/cars\/[^\/]+$/, // /lang/cars/slug
    /^\/[a-z]{2}\/location-voiture\/[^\/]+\/[^\/]+$/, // /lang/location-voiture/city/slug
    /^\/[a-z]{2}\/rent-car\/[^\/]+\/[^\/]+$/, // /lang/rent-car/city/slug
    /^\/[a-z]{2}\/تأجير-سيارة\/[^\/]+\/[^\/]+$/ // /lang/تأجير-سيارة/city/slug
  ]

  return carDetailPatterns.some(pattern => pattern.test(pathname))
}

/**
 * Check if a URL path is a location-based car detail page
 */
export function isLocationCarDetailPage(pathname: string): boolean {
  // Check for location-based car detail patterns
  const locationCarDetailPatterns = [
    /^\/[a-z]{2}\/location-voiture\/[^\/]+\/[^\/]+$/, // /lang/location-voiture/city/slug
    /^\/[a-z]{2}\/rent-car\/[^\/]+\/[^\/]+$/, // /lang/rent-car/city/slug
    /^\/[a-z]{2}\/تأجير-سيارة\/[^\/]+\/[^\/]+$/ // /lang/تأجير-سيارة/city/slug
  ]

  return locationCarDetailPatterns.some(pattern => pattern.test(pathname))
}

/**
 * Translate a URL path when language changes
 */
export function translateUrlPath(pathname: string, fromLang: string, toLang: string): string {
  // Extract the path without language prefix
  const pathWithoutLang = pathname.replace(/^\/[a-z]{2}\//, '/')
  
  // Handle car detail pages
  if (isCarDetailPage(pathname)) {
    if (isLocationCarDetailPage(pathname)) {
      // Location-based car detail page
      const urlInfo = extractCarDetailFromUrl(pathname)
      if (urlInfo.carSlug && urlInfo.locationSlug) {
        const translatedSlug = translateLocationCarSlug(urlInfo.carSlug, fromLang, toLang)
        
        // Generate new URL based on target language
        const prefixes = {
          fr: 'location-voiture',
          en: 'rent-car',
          ar: 'تأجير-سيارة'
        }
        const newPrefix = prefixes[toLang as keyof typeof prefixes] || prefixes.fr
        
        return `/${toLang}/${newPrefix}/${urlInfo.locationSlug}/${translatedSlug}`
      }
    } else {
      // Regular car detail page
      const pathParts = pathWithoutLang.split('/')
      if (pathParts.length >= 3 && pathParts[1] === 'cars') {
        const currentSlug = pathParts[2]
        const translatedSlug = translateCarSlug(currentSlug, fromLang, toLang)
        return `/${toLang}/cars/${translatedSlug}`
      }
    }
  }
  
  // For non-car pages, just change the language prefix
  return `/${toLang}${pathWithoutLang}`
}
